<template>
  <ResponsiveModal
    :variant="variant"
    :title="!maxSmall ? $t('components.task_details.actions.title') : undefined"
    :withClose="false"
    :showFooter="false"
    alignItems="center"
  >
    <slot />
    <template #content>
      <DialogClose class="flex flex-col p-4 md:px-5 md:py-6 gap-2">
        <ButtonList :items="actions" size="xl" @click="$event()" />
      </DialogClose>
    </template>
  </ResponsiveModal>
  <ShareModal
    :open="openShareTask"
    @update:open="openShareTask = $event"
    :task="task"
  />
  <DeleteModal
    v-if="!isArchived"
    :open="openDeleteTask"
    @update:open="openDeleteTask = $event"
    :title="$t('components.task_details.delete.title')"
    :description="$t('components.task_details.delete.description')"
    :confirmLabel="$t('components.task_details.delete.button')"
    @delete="$emit('delete')"
  />
</template>

<script setup>
import { computed, ref } from "vue";
import { ButtonList } from "@/SharedComponents/ButtonList";
import { DialogClose } from "@/SharedComponents/Dialog";
import { DeleteModal, ShareModal } from "@/SharedComponents/Modals";
import { ResponsiveModal } from "@/SharedComponents/ResponsiveModal";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useI18n } from "vue-i18n";

const { maxSmall } = useBreakpoints();
const { t } = useI18n();

const emit = defineEmits([
  "logTime",
  "logMaterial",
  "addChecklist",
  "translate",
  "delete",
]);

const props = defineProps({
  variant: { type: String, required: false },
  task: { type: Object, required: true },
  canEdit: { type: Boolean, default: false },
  canLogTime: { type: Boolean, default: false },
  canLogMaterial: { type: Boolean, default: false },
  canCreateChecklist: { type: Boolean, default: false },
  isArchived: { type: Boolean, default: false },
  readonly: { type: Boolean, required: false },
});

const openShareTask = ref(undefined);
const openDeleteTask = ref(undefined);

const actions = computed(() => {
  const actions = [];
  if (props.canLogTime && !props.readonly) {
    actions.push({
      id: "log_time",
      icon: "clock-add",
      label: t("components.task_details.button.log_time"),
      event: () => emit("logTime"),
    });
  }
  if (props.canLogMaterial && !props.readonly) {
    actions.push({
      id: "log_material",
      icon: "euro-circle",
      label: t("components.task_details.button.log_material"),
      event: () => emit("logMaterial"),
    });
  }
  if (
    !props.task.checklist?.items?.length &&
    props.canCreateChecklist &&
    !props.readonly
  ) {
    actions.push({
      id: "add_checklist",
      icon: "checklist",
      label: t("components.task_details.button.add_checklist"),
      event: () => emit("addChecklist"),
    });
  }
  actions.push({
    id: "translate",
    icon: "translate",
    label: t("components.task_details.button.translate"),
    event: () => emit("translate"),
  });
  if (props.canEdit) {
    actions.push({
      id: "share",
      icon: "link-default",
      label: t("components.task_details.share.title"),
      event: () => (openShareTask.value = true),
    });
  }
  if (props.canEdit && !props.isArchived) {
    actions.push({
      id: "delete",
      icon: "delete",
      label: t("components.task_details.delete.button"),
      variant: "primary",
      type: "destructive",
      event: () => (openDeleteTask.value = true),
    });
  }
  return actions;
});
</script>
